import StarsIcon from "@mui/icons-material/Stars";
import {
  Avatar,
  Chip,
  Grid,
  Box,
  Typography,
  Card,
  CardContent,
  Skeleton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useState, useEffect } from "react";
import "../style.css";
import "./Profile.css";
import axios from "axios";
import { API_URL } from "../../consts";
import ChipList from "../common/ChipList";
import Level from "../../Assets/Level.svg";
import { useParams } from "react-router";
import { useDispatch } from "react-redux";

const StyledCard = styled(Card)(({ theme }) => ({
  background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.98) 100%)",
  backdropFilter: "blur(10px)",
  borderRadius: "16px",
  border: "1px solid rgba(226,232,240,0.8)",
  boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
  },
}));

const ProfileHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(2),
  padding: theme.spacing(3),
  background: "linear-gradient(135deg, rgba(99,102,241,0.05) 0%, rgba(139,92,246,0.05) 100%)",
  borderRadius: "16px",
  marginBottom: theme.spacing(3),
  [theme.breakpoints.up("sm")]: {
    flexDirection: "row",
    alignItems: "flex-start",
    textAlign: "left",
  },
}));

const StatsBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5, 2),
  borderRadius: "12px",
  background: "rgba(255,255,255,0.7)",
  border: "1px solid rgba(226,232,240,0.8)",
  transition: "all 0.2s ease",
  "&:hover": {
    background: "rgba(255,255,255,0.9)",
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  },
}));

const InfoCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: "12px",
  background: "rgba(255,255,255,0.8)",
  border: "1px solid rgba(226,232,240,0.6)",
  marginBottom: theme.spacing(2),
  transition: "all 0.2s ease",
  "&:hover": {
    borderColor: "rgba(99,102,241,0.3)",
    boxShadow: "0 4px 12px rgba(99,102,241,0.1)",
  },
}));

const OthersProfile = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  let { id } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    first_name: "",
    last_name: "",
    points: 0,
    level: 0,
    level_title: "",
    profile_image_url: "",
    title: "",
    tags: [],
    most_interested: [],
  });

  useEffect(() => {
    fetchUserProfile();
  }, [id]);

  const fetchUserProfile = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const url = `${API_URL}/users/${id}`;
      const response = await axios.get(url);
      
      if (response.data) {
        setData({
          first_name: response.data.first_name || "",
          last_name: response.data.last_name || "",
          points: response.data.points || 0,
          level: response.data.level || 0,
          level_title: response.data.level_title || "",
          profile_image_url: response.data.profile_image_url || "",
          title: response.data.title || "",
          tags: response.data.tags || [],
          most_interested: response.data.most_interested || [],
          user_name: response.data.user_name || "",
        });
      }
    } catch (err) {
      console.error("Error fetching user profile:", err);
      if (err.response?.status === 401) {
        dispatch({ type: "SET_UNAUTHORIZED" });
        setError("Unauthorized access");
      } else if (err.response?.status === 403) {
        setError("User not found");
      } else {
        setError(err.response?.data?.error || "Failed to load profile. Please try again later.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getDisplayName = () => {
    const firstName = data.first_name?.trim() || "";
    const lastName = data.last_name?.trim() || "";
    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }
    return data.user_name || "User";
  };

  const getColorForUser = (name) => {
    const colors = [
      "#6366f1", // Indigo
      "#8b5cf6", // Violet
      "#06b6d4", // Cyan
      "#10b981", // Emerald
      "#f59e0b", // Amber
      "#ef4444", // Red
    ];
    const firstLetter = name?.[0] || "A";
    const colorIndex = firstLetter.charCodeAt(0) % colors.length;
    return colors[colorIndex];
  };

  const formatPoints = (points) => {
    if (points >= 1000) {
      return `${(points / 1000).toFixed(1)}k`;
    }
    return points?.toString() || "0";
  };

  const displayName = getDisplayName();
  const userColor = getColorForUser(displayName);

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, rgba(248,250,252,0.8) 0%, rgba(255,255,255,0.9) 100%)",
          pt: { xs: 2, sm: 4, md: 6 },
          px: { xs: 2, sm: 3, md: 4 },
          pb: 4,
        }}
      >
        <Box sx={{ maxWidth: "1200px", mx: "auto" }}>
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2, mb: 3 }} />
          <Skeleton variant="text" height={40} width="60%" sx={{ mb: 2 }} />
          <Skeleton variant="text" height={30} width="40%" />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, rgba(248,250,252,0.8) 0%, rgba(255,255,255,0.9) 100%)",
          pt: { xs: 2, sm: 4, md: 6 },
          px: { xs: 2, sm: 3, md: 4 },
          pb: 4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Card
          sx={{
            p: 4,
            maxWidth: 500,
            textAlign: "center",
            borderRadius: "16px",
            background: "rgba(255,255,255,0.95)",
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, color: "error.main", fontWeight: 600 }}>
            {error}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please check the URL or try again later.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, rgba(248,250,252,0.8) 0%, rgba(255,255,255,0.9) 100%)",
        pt: { xs: 2, sm: 4, md: 6 },
        px: { xs: 2, sm: 3, md: 4 },
        pb: { xs: 8, sm: 4 },
      }}
    >
      <Box sx={{ maxWidth: "1200px", mx: "auto" }}>
        {/* Profile Header */}
        <StyledCard sx={{ mb: 4 }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <ProfileHeader>
              {/* Avatar */}
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {data.profile_image_url ? (
                  <Avatar
                    src={data.profile_image_url}
                    alt={displayName}
                    sx={{
                      width: { xs: 100, sm: 120 },
                      height: { xs: 100, sm: 120 },
                      border: `4px solid ${userColor}40`,
                      boxShadow: `0 4px 20px ${userColor}30`,
                    }}
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: { xs: 100, sm: 120 },
                      height: { xs: 100, sm: 120 },
                      background: `linear-gradient(135deg, ${userColor} 0%, ${userColor}dd 100%)`,
                      color: "#fff",
                      fontSize: { xs: "40px", sm: "48px" },
                      fontWeight: "bold",
                      boxShadow: `0 4px 20px ${userColor}30`,
                    }}
                  >
                    {displayName?.[0]?.toUpperCase() || "?"}
                  </Avatar>
                )}
                {data.title && (
                  <Chip
                    label={data.title.toUpperCase()}
                    size="small"
                    sx={{
                      background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "11px",
                      height: "24px",
                      boxShadow: "0 2px 8px rgba(99,102,241,0.3)",
                    }}
                  />
                )}
              </Box>

              {/* Name and Info */}
              <Box sx={{ flex: 1, width: "100%", textAlign: { xs: "center", sm: "left" } }}>
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "1.75rem", sm: "2.25rem", md: "2.5rem" },
                    background: "linear-gradient(135deg, #1a202c 0%, #6366f1 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    mb: 1,
                  }}
                >
                  {displayName}
                </Typography>
                
                {data.user_name && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(71,85,105,0.6)",
                      fontSize: { xs: "13px", sm: "14px" },
                      mb: 2,
                      fontFamily: "monospace",
                    }}
                  >
                    @{data.user_name}
                  </Typography>
                )}

                {/* Stats Row */}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: { xs: 1.5, sm: 2 },
                    mt: 2,
                    justifyContent: { xs: "center", sm: "flex-start" },
                  }}
                >
                  <StatsBox>
                    <Avatar
                      src={Level}
                      sx={{
                        width: 32,
                        height: 32,
                        background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                      }}
                    />
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          color: "rgba(71,85,105,0.6)",
                          fontSize: "11px",
                          fontWeight: 500,
                        }}
                      >
                        Level
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          color: "rgba(30,41,59,0.9)",
                          fontSize: "14px",
                        }}
                      >
                        {data.level || 1}
                      </Typography>
                    </Box>
                  </StatsBox>

                  <StatsBox>
                    <StarsIcon
                      sx={{
                        fontSize: 28,
                        color: "#6366f1",
                      }}
                    />
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          color: "rgba(71,85,105,0.6)",
                          fontSize: "11px",
                          fontWeight: 500,
                        }}
                      >
                        Points
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          color: "rgba(30,41,59,0.9)",
                          fontSize: "14px",
                        }}
                      >
                        {formatPoints(data.points)}
                      </Typography>
                    </Box>
                  </StatsBox>

                  {data.level_title && (
                    <StatsBox>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            color: "#fff",
                            fontSize: "14px",
                            fontWeight: 700,
                          }}
                        >
                          ✨
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            color: "rgba(71,85,105,0.6)",
                            fontSize: "11px",
                            fontWeight: 500,
                          }}
                        >
                          Title
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color: "rgba(30,41,59,0.9)",
                            fontSize: "14px",
                          }}
                        >
                          {data.level_title}
                        </Typography>
                      </Box>
                    </StatsBox>
                  )}
                </Box>
              </Box>
            </ProfileHeader>
          </CardContent>
        </StyledCard>

        {/* Profile Information Cards */}
        <Grid container spacing={4}>
          {/* Personal Information */}
          <Grid item xs={12} md={6}>
            <StyledCard sx={{ mb: { xs: 3, md: 0 } }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "1.1rem", sm: "1.25rem" },
                    mb: 3,
                    color: "rgba(30,41,59,0.9)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 4,
                      height: 24,
                      borderRadius: "2px",
                      background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                    }}
                  />
                  Personal Information
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                  <InfoCard>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: "rgba(71,85,105,0.6)",
                        fontSize: "12px",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        mb: 1,
                      }}
                    >
                      First Name
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: "rgba(30,41,59,0.9)",
                        fontSize: "16px",
                      }}
                    >
                      {data.first_name || "—"}
                    </Typography>
                  </InfoCard>

                  <InfoCard>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: "rgba(71,85,105,0.6)",
                        fontSize: "12px",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        mb: 1,
                      }}
                    >
                      Last Name
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: "rgba(30,41,59,0.9)",
                        fontSize: "16px",
                      }}
                    >
                      {data.last_name || "—"}
                    </Typography>
                  </InfoCard>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>

          {/* Interests & Tags */}
          <Grid item xs={12} md={6}>
            <StyledCard>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "1.1rem", sm: "1.25rem" },
                    mb: 3,
                    color: "rgba(30,41,59,0.9)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 4,
                      height: 24,
                      borderRadius: "2px",
                      background: "linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)",
                    }}
                  />
                  Interests & Tags
                </Typography>

                {data.most_interested && data.most_interested.length > 0 ? (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: "rgba(71,85,105,0.6)",
                        fontSize: "12px",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        mb: 1.5,
                      }}
                    >
                      Areas of Interest
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      {data.most_interested.map((interest, idx) => (
                        <Chip
                          key={idx}
                          label={interest}
                          size="small"
                          sx={{
                            background: "rgba(139,92,246,0.1)",
                            color: "rgba(139,92,246,0.9)",
                            fontWeight: 500,
                            fontSize: "13px",
                            height: "28px",
                            border: "1px solid rgba(139,92,246,0.2)",
                            "&:hover": {
                              background: "rgba(139,92,246,0.15)",
                            },
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                ) : null}

                {data.tags && data.tags.length > 0 ? (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: "rgba(71,85,105,0.6)",
                        fontSize: "12px",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        mb: 1.5,
                      }}
                    >
                      Popular Tags
                    </Typography>
                    <ChipList list={data.tags} />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      p: 3,
                      textAlign: "center",
                      borderRadius: "12px",
                      background: "rgba(71,85,105,0.05)",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(71,85,105,0.5)",
                        fontStyle: "italic",
                      }}
                    >
                      No interests or tags specified
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default OthersProfile;
